'use client'
import { useEffect, useState } from 'react'
import { IconExclamationCircle } from '@tabler/icons-react'
import { AxiosError, isAxiosError } from 'axios'
import { useRouter, useSearchParams } from 'next/navigation'

import Button from 'shared/ui/Button'
import Input from 'shared/ui/form/Input'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { PATHS } from 'shared/utils/paths'

import { resetPassword, setNewPassword } from '../api/auth'
import {
  validateNewPassword,
  validateNewPasswordValues
} from '../validation/resetPasswordSchema'

import styles from './ResetPasswordForm.module.scss'

const defaultValues = {
  password: '',
  passwordConfirmation: ''
}

const ResetPasswordForm = () => {
  const [values, setValues] = useState(defaultValues)
  const [valid, setValid] = useState(false)
  const [linkError, setLinkError] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const router = useRouter()
  const searchParams = useSearchParams()

  const handleValueChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const email = searchParams?.get('email') || ''
  const token = searchParams?.get('token') || ''
  const expires = searchParams?.get('expires') || ''

  const queryParams = { email, token, expires }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await setNewPassword({ ...queryParams, ...values })
      router.push(PATHS.home)
      toast.success('Hasło zostało pomyślnie zmienione')
    } catch (err) {
      const errorResponse = isAxiosError(err)
        ? (err as AxiosError).response
        : null

      if (errorResponse?.status === 422) {
        setLinkError(true)
      } else {
        toast.error(getApiErrors(err))
      }
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (values) {
      validateNewPasswordValues(values, setValid)
    }
  }, [values])

  const retryPasswordReset = async () => {
    setIsLoading(true)
    try {
      await resetPassword({ email })
      setEmailSent(true)
      toast.success('Email do resetu hasła został wysłany na Twoją skrzynkę')
    } catch (err) {
      console.error(err)
      toast.error(getApiErrors(err))
    }
  }

  if (linkError) {
    return (
      <div className={styles.errorMessage}>
        <IconExclamationCircle className={styles.errorIcon} size={100} />
        <div className={styles.errorText}>
          Twój link stracił wazność lub jest uszkodzony. Spróbuj zresetować
          hasło ponownie.
        </div>
        <Button
          id='reset_password_retry_reset_button'
          fullWidth
          variant='primary'
          size='large'
          type='button'
          onClick={retryPasswordReset}
          disabled={emailSent}
        >
          {emailSent ? 'Sprawdź swoją skrzynkę' : 'Wyślij nowy link'}
        </Button>
      </div>
    )
  }

  return (
    <form noValidate className={styles.formWrapper} onSubmit={handleSubmit}>
      <div className={styles.formHeader}>Zmiana hasła</div>
      <Input
        id='reset_password_new_password'
        required
        withPasswordHint
        label='Nowe hasło'
        type='password'
        name='password'
        onChange={handleValueChange}
        value={values.password}
        validate={validateNewPassword(values)}
      />
      <Input
        id='reset_password_new_password_confirmation'
        label='Potwierdź nowe hasło'
        type='password'
        name='passwordConfirmation'
        onChange={handleValueChange}
        value={values.passwordConfirmation}
        validate={validateNewPassword(values)}
      />
      <div className={styles.buttonsWrapper}>
        <Button
          id='reset_password_save_password_button'
          fullWidth
          variant='primary'
          size='large'
          type='submit'
          disabled={!valid || isLoading}
        >
          Zapisz hasło
        </Button>
      </div>
    </form>
  )
}

export default ResetPasswordForm
